import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  ClickAwayListener,
  Fade,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { getBrandSearchOptions, formatBrandSearchLabel } from "./utils/helpersOfBrands";

const MAX_RENDERED_OPTIONS = 200;

interface Props {
  rows: any[];
  value: string[];
  onChange: (labels: string[]) => void;
}

// "Brand directory" multi-select search, per spec: focusing (or typing into)
// the field opens a popup of matching brands, each with its own checkbox,
// plus "Select all" and a found-count. Ticking brands only stages a selection — nothing is applied
// to the table until Enter is pressed. Enter with nothing ticked applies
// every currently-matching brand (equivalent to a plain substring filter).
// Options are labeled "Brand Name (Brand ID)" (same format as the plain
// BrandNameSearch on the other two tabs) so typing matches either the name
// or the numeric id, and so brands sharing a name stay distinguishable as
// separate ticks. `value`/`onChange` carry these formatted labels — the
// consuming AllBrands.tsx matches rows against the same format via
// formatBrandSearchLabel. Shared across all three Brands tabs.
const BrandDirectorySearch = ({ rows, value, onChange }: Props) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [ticked, setTicked] = useState<Set<string>>(new Set(value));
  // Snapshot of `ticked` taken at the moment the popup opens — same pattern
  // as FilterComponent.tsx's Status/Company/etc. filters: already-selected
  // brands sort to the top so a large list stays easy to re-find them in,
  // but the ordering is frozen for the session (a snapshot, not `ticked`
  // itself) so rows don't jump around while the user is actively ticking/
  // unticking inside the same open popup.
  const [pinnedToTop, setPinnedToTop] = useState<Set<string>>(new Set());

  // Reflect an external reset (e.g. "Clear Filters") back into local state.
  useEffect(() => {
    if (value.length === 0) {
      setQuery("");
      setTicked(new Set());
      setPinnedToTop(new Set());
    }
  }, [value]);

  const openPopup = () => {
    setOpen((prevOpen) => {
      if (!prevOpen) setPinnedToTop(new Set(ticked));
      return true;
    });
  };

  const brandNameOptions = useMemo(() => getBrandSearchOptions(rows), [rows]);

  // Secondary label per brand — the company name disambiguates same-named
  // brands, similar to the region shown in the Figma "Brand directory" mockup.
  // Keyed by the same formatted "Brand Name (Brand ID)" label the options
  // list uses, since brand_id is now what makes each option unique.
  const companyByBrandName = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((row: any) => {
      const label = formatBrandSearchLabel(row);
      if (row?.brand_name && !map.has(label)) {
        map.set(label, row.company_name ?? "");
      }
    });
    return map;
  }, [rows]);

  const filteredOptions = useMemo(() => {
    const lowerQuery = query.trim().toLowerCase();
    const matching = !lowerQuery
      ? brandNameOptions
      : brandNameOptions.filter((name) => name.toLowerCase().includes(lowerQuery));

    if (pinnedToTop.size === 0) return matching;

    const pinned = matching.filter((name) => pinnedToTop.has(name));
    if (pinned.length === 0) return matching;
    const rest = matching.filter((name) => !pinnedToTop.has(name));
    return [...pinned, ...rest];
  }, [brandNameOptions, query, pinnedToTop]);

  // "Select all" ticks a snapshot of filteredOptions at click time. If the
  // underlying brands data refreshes afterward (e.g. after Try to Connect /
  // Test Again / Approve, all of which re-fetch the Brands list) while the
  // popup is still open, the match set can grow — leaving "Select all" stuck
  // indeterminate because the newly-appeared brands were never ticked, with
  // no way to spot which ones since only the first MAX_RENDERED_OPTIONS
  // render. Re-sync: whenever the *underlying data* changes (brandNameOptions
  // — not the query) and everything that matched under today's query was
  // ticked (i.e. "select all" was active for that query), extend the tick
  // set to cover the newly-appeared matches too, so "select all means all"
  // keeps holding instead of silently drifting.
  //
  // Deliberately keyed off brandNameOptions (derived only from `rows`), NOT
  // filteredOptions (derived from `rows` AND `query`) — depending on
  // filteredOptions would also re-run this on every keystroke, and widening
  // the search after a fully-ticked narrow query (e.g. "casino") would then
  // wrongly balloon the selection to everything instead of staying scoped to
  // what the user actually ticked.
  const prevBrandNameOptionsRef = useRef(brandNameOptions);
  useEffect(() => {
    const prevOptions = prevBrandNameOptionsRef.current;
    if (prevOptions !== brandNameOptions) {
      const lowerQuery = query.trim().toLowerCase();
      const prevMatching = !lowerQuery
        ? prevOptions
        : prevOptions.filter((name) => name.toLowerCase().includes(lowerQuery));
      const wasFullySelected =
        prevMatching.length > 0 && prevMatching.every((name) => ticked.has(name));
      if (wasFullySelected) {
        setTicked((prev) => new Set([...prev, ...filteredOptions]));
      }
    }
    prevBrandNameOptionsRef.current = brandNameOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandNameOptions]);

  const pinnedVisibleCount = filteredOptions.filter((name) => pinnedToTop.has(name)).length;
  const visibleOptions = filteredOptions.slice(0, MAX_RENDERED_OPTIONS);
  const allFilteredTicked =
    filteredOptions.length > 0 && filteredOptions.every((name) => ticked.has(name));

  const toggleOne = (name: string) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setTicked((prev) => {
      if (allFilteredTicked) {
        const next = new Set(prev);
        filteredOptions.forEach((name) => next.delete(name));
        return next;
      }
      return new Set([...prev, ...filteredOptions]);
    });
  };

  const applyAndClose = () => {
    const applied = ticked.size > 0 ? Array.from(ticked) : filteredOptions;
    onChange(applied);
    setOpen(false);
  };

  const handleClear = () => {
    setQuery("");
    setTicked(new Set());
    onChange([]);
    setOpen(false);
  };

  // Enter applies the staged ticks. This lives on the wrapper Box rather than
  // on the TextField because ticking a checkbox moves focus into the popup —
  // the row-level onMouseDown guard below does not reliably stop that for
  // MUI's SwitchBase <input>. A checkbox is not a descendant of the TextField,
  // so its keydown never bubbled through a handler bound there and Enter was
  // silently dropped, leaving the table unfiltered. The Popper renders inside
  // this same Box (disablePortal), so binding here catches Enter from the
  // search field and from anywhere in the dropdown alike.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" || !open) return;
    e.preventDefault();
    applyAndClose();
    (e.target as HTMLElement)?.blur?.();
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ width: 450 }} onKeyDown={handleKeyDown}>
        <TextField
          fullWidth
          size="small"
          color="secondary"
          label="Brand Name"
          placeholder="Search brands…"
          data-tracker="620"
          value={query}
          onFocus={openPopup}
          onChange={(e) => {
            setQuery(e.target.value);
            openPopup();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (query || value.length > 0) && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          // Without this, Popper portals its content to document.body — outside
          // the <Box ref={anchorRef}> subtree the ClickAwayListener above wraps —
          // so every click inside the dropdown (ticking a checkbox included)
          // reads as an "outside" click and closes the popup before the tick
          // can register. Keeping it in the same subtree fixes that.
          disablePortal
          sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth ?? 280 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <Paper elevation={4} sx={{ mt: 0.5, overflow: "hidden" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  // Keeps focus on the search TextField instead of letting the
                  // browser's default mousedown behavior move it to this row —
                  // otherwise Enter (after ticking) goes to whichever row last
                  // stole focus instead of triggering handleKeyDown, forcing a
                  // second Enter after clicking back into the field.
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{ px: 1.5, py: 0.75, borderBottom: "1px solid #eee" }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={allFilteredTicked}
                        indeterminate={
                          !allFilteredTicked && filteredOptions.some((name) => ticked.has(name))
                        }
                        onChange={toggleSelectAll}
                        disabled={filteredOptions.length === 0}
                      />
                    }
                    label={<Typography variant="body2">Select all</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {filteredOptions.length} found
                  </Typography>
                </Stack>

                <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                  {visibleOptions.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ p: 2, color: "text.secondary", textAlign: "center" }}
                    >
                      {query.trim() ? `No brands match "${query.trim()}"` : "No brands available"}
                    </Typography>
                  ) : (
                    visibleOptions.map((name, index) => (
                      <Stack
                        key={name}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        // Same focus-preservation as the "Select all" row above.
                        onMouseDown={(e) => e.preventDefault()}
                        sx={{
                          px: 1.5,
                          "&:hover": { backgroundColor: "#FAFAFA" },
                          ...(pinnedVisibleCount > 0 &&
                            pinnedVisibleCount < visibleOptions.length &&
                            index === pinnedVisibleCount - 1 && {
                              borderBottom: "1px dashed #ddd",
                            }),
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0, flexGrow: 1, minWidth: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              color="secondary"
                              checked={ticked.has(name)}
                              onChange={() => toggleOne(name)}
                            />
                          }
                          label={
                            <Typography variant="body2" noWrap>
                              {name}
                            </Typography>
                          }
                        />
                        {companyByBrandName.get(name) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ pl: 1, flexShrink: 0 }}
                          >
                            {companyByBrandName.get(name)}
                          </Typography>
                        )}
                      </Stack>
                    ))
                  )}
                  {filteredOptions.length > MAX_RENDERED_OPTIONS && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", p: 1, color: "text.secondary", textAlign: "center" }}
                    >
                      Showing first {MAX_RENDERED_OPTIONS} of {filteredOptions.length} — refine
                      your search to see more.
                    </Typography>
                  )}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 1.5,
                    py: 0.75,
                    borderTop: "1px solid #eee",
                    color: "text.secondary",
                  }}
                >
                  Press Enter to apply {ticked.size > 0 ? `(${ticked.size} ticked)` : ""}
                </Typography>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default BrandDirectorySearch;
